import React from 'react';
import { X, Copy, Search, Loader, GitMerge, Phone, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

/**
 * "Find Duplicate Surgeons" modal — surgeon analogue of
 * DuplicateFinderModal. The backend grouping heuristic is simply
 * same-last-name + same-first-initial (stated in the hint line), so
 * groups can contain genuinely different people; the user reviews
 * contacts/specialty before choosing which record to keep.
 * onMerge(surgeon, other) opens SurgeonMergeModal with that pair.
 * Presentational only; mounted conditionally by SurgeonsDatabase, so the
 * Radix Dialog is `open` — Escape and overlay-click route to onClose.
 */
const SurgeonDuplicateFinderModal = ({
  dupeGroups, dupeLoading,
  onSearch, onClose, onMerge,
}) => {
  const fullName = function(s) {
    return [s.title, s.first_name, s.last_name].filter(Boolean).join(' ');
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl flex flex-col overflow-y-auto" style={{maxHeight:'90vh'}}>
        <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
          <div className="flex items-center space-x-2">
            <Copy className="h-5 w-5 text-amber-600" />
            <DialogTitle>Find Duplicate Surgeons</DialogTitle>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 flex items-center shrink-0">
          <p className="text-sm text-gray-500 flex-1">Groups surgeons sharing the same last name and first initial.</p>
          <button onClick={onSearch} disabled={dupeLoading}
            className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium flex items-center space-x-1 disabled:opacity-50">
            {dupeLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span>{dupeLoading ? 'Searching...' : 'Search'}</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!dupeLoading && dupeGroups.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Copy className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Click Search to find potential duplicates</p>
            </div>
          )}

          {dupeGroups.map(function(group, gi) {
            return (
              <div key={gi} className="border border-amber-200 rounded-xl overflow-hidden">
                <div className="bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700 uppercase tracking-wide">
                  Group {gi + 1} — {group.length} possible duplicates
                </div>
                <div className="divide-y divide-gray-100">
                  {group.map(function(surgeon, ci) {
                    return (
                      <div key={surgeon.id} className="px-4 py-3 flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">{fullName(surgeon)}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{surgeon.specialty}{surgeon.qualifications ? ' · ' + surgeon.qualifications : ''}</div>
                          <div className="text-xs text-gray-400">{surgeon.id}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(surgeon.contacts || []).slice(0, 3).map(function(c, ci2) {
                              var Icon = c.type === 'email' ? Mail : Phone;
                              return (
                                <span key={ci2} className="flex items-center space-x-0.5 text-xs text-gray-500">
                                  <Icon className="h-3 w-3 text-gray-400" />
                                  <span>{c.value}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-1 ml-3 shrink-0">
                          {group.filter(function(_, j) { return j !== ci; }).map(function(other) {
                            return (
                              <button key={other.id} onClick={function() { onMerge(surgeon, other); }}
                                className="flex items-center space-x-1 px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg text-xs font-medium">
                                <GitMerge className="h-3 w-3" />
                                <span>Keep this, merge {other.id}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {!dupeLoading && dupeGroups.length > 0 && (
            <p className="text-center text-xs text-gray-400">{dupeGroups.length} group{dupeGroups.length !== 1 ? 's' : ''} found</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SurgeonDuplicateFinderModal;
